import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, Clock3, MapPin } from "lucide-react";

const LOGO_URL =
  "https://customer-assets-agu9un31.emergentagent.net/job_ad37d109-0510-49cf-8bc7-b236afa36719/artifacts/2a2b75d6da9fff77_file_00000000b8448210b1d7027daea590bc.png";
const HERO_BG =
  "https://images.unsplash.com/photo-1562797327-93c461a71ef2?crop=entropy&cs=srgb&fm=jpg&q=85";

const reveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.35 + i * 0.16, duration: 1, ease: [0.215, 0.61, 0.355, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.9 + i * 0.12, duration: 0.8, ease: [0.215, 0.61, 0.355, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.1]);

  const scrollToForm = () => {
    document.querySelector("#request-service")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header ref={ref} className="relative flex min-h-screen flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt="Restored home exterior at dusk"
          className="h-[120%] w-full scale-110 object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/85 via-[#08080A]/55 to-[#08080A]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.16),transparent_60%)]" />
      </motion.div>

      <nav className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-4 pt-6 sm:px-6" data-testid="hero-nav">
        <p className="font-mono-data text-[10px] uppercase tracking-[0.35em] text-slate-300 sm:text-xs">
          Redemption Restoration
        </p>
        <a
          href="tel:9204152145"
          data-testid="nav-call-link"
          className="flex items-center gap-2 font-mono-data text-xs tracking-widest text-purple-300 transition-colors hover:text-purple-200 sm:text-sm"
        >
          <Phone className="h-3.5 w-3.5" /> 920-415-2145
        </a>
      </nav>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 mx-auto flex w-full max-w-5xl flex-1 flex-col items-center justify-center px-4 py-16 text-center sm:px-6"
      >
        <motion.img
          src={LOGO_URL}
          alt="Redemption Restoration logo"
          data-testid="hero-logo"
          initial={{ opacity: 0, scale: 0.92, filter: "blur(8px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.1, ease: [0.215, 0.61, 0.355, 1] }}
          className="mb-8 w-44 drop-shadow-[0_0_45px_rgba(139,92,246,0.35)] sm:w-56"
        />

        <motion.p
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mb-5 font-mono-data text-[10px] uppercase tracking-[0.4em] text-purple-300 sm:text-xs"
          data-testid="hero-eyebrow"
        >
          Fox Valley · Wisconsin &amp; Beyond
        </motion.p>

        <h1 className="font-serif-display text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-7xl" data-testid="hero-title">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={reveal} initial="hidden" animate="visible" className="text-silver-gradient block">
              Restoring Properties.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              custom={1}
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="block italic text-purple-400"
            >
              Rebuilding Lives.
            </motion.span>
          </span>
        </h1>

        <motion.p
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mt-6 max-w-xl text-sm font-light leading-relaxed text-slate-400 sm:text-base"
          data-testid="hero-subtitle"
        >
          When water, fire, mold, or biohazard strikes your home, one call brings a certified crew to
          your door — and we stay until your house is put back together, better than before.
        </motion.p>

        <motion.div
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <a
            href="tel:9204152145"
            data-testid="hero-call-button"
            className="group flex items-center gap-3 rounded-full bg-purple-600 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-white shadow-[0_0_40px_rgba(139,92,246,0.4)] transition-all duration-300 hover:bg-purple-500 hover:shadow-[0_0_60px_rgba(139,92,246,0.6)]"
          >
            <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
            24/7 Emergency — Call Now
          </a>
          <button
            onClick={scrollToForm}
            data-testid="hero-request-service-button"
            className="rounded-full border border-slate-400/30 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-slate-200 transition-all duration-300 hover:border-purple-400/60 hover:text-white"
          >
            Request Service
          </button>
        </motion.div>

        <motion.div
          custom={3}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
          data-testid="hero-trust-badges"
        >
          {[
            { icon: Clock3, label: "24/7 Rapid Response" },
            { icon: ShieldCheck, label: "Licensed & Insured" },
            { icon: MapPin, label: "Fox Valley, WI" },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-[0.25em] text-slate-500 sm:text-xs">
              <Icon className="h-3.5 w-3.5 text-purple-400" /> {label}
            </span>
          ))}
        </motion.div>
      </motion.div>
    </header>
  );
}
